import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaRoute, FaCoffee } from 'react-icons/fa';
import { IoIosHelpCircle } from 'react-icons/io';
import { motion } from 'framer-motion';
import * as Dialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import confetti from 'canvas-confetti';

const GRID_SIZE = 10;
const RACK_SIZE = 7;
const BOTTOM_ROW = GRID_SIZE - 1;
const TOP_ROW = 0;
const CELL_SIZE = 30;

// Standard Scrabble-style letter distribution (no blanks)
const LETTER_COUNTS = {
  A: 9, B: 2, C: 2, D: 4, E: 12, F: 2, G: 3, H: 2, I: 9, J: 1,
  K: 1, L: 4, M: 2, N: 6, O: 8, P: 2, Q: 1, R: 6, S: 4, T: 6,
  U: 4, V: 2, W: 2, X: 1, Y: 2, Z: 1,
};

const LETTER_POOL = Object.entries(LETTER_COUNTS)
  .flatMap(([letter, count]) => Array(count).fill(letter));

function randomLetter() {
  return LETTER_POOL[Math.floor(Math.random() * LETTER_POOL.length)];
}

function makeRack() {
  return Array.from({ length: RACK_SIZE }, () => randomLetter());
}

const WordGameContainer = styled.div`
  min-height: 100vh;
  padding: 16px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AnimatedCoffeeButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  position: absolute;
  top: 0;
  right: 0;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 4px;
  text-align: center;
  width: 100%;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  width: 340px;
  max-width: 100%;
`;

const Subtitle = styled.p`
  margin: 0 0 16px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const StatusRow = styled.div`
  width: 320px;
  max-width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const RackRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: nowrap;
`;

const RackTile = styled(motion.div)`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: white;
  background-color: ${props => (props.isSelected ? '#e0106e' : '#541930')};
  cursor: grab;
  user-select: none;
  box-shadow: ${props => (props.isSelected ? '0 0 0 3px rgba(224, 16, 110, 0.35)' : 'none')};
`;

const RackSlotEmpty = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 2px dashed #ccc;
  background-color: transparent;
`;

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(${GRID_SIZE}, ${CELL_SIZE}px);
  grid-template-rows: repeat(${GRID_SIZE}, ${CELL_SIZE}px);
  gap: 2px;
`;

const cellBackground = ({ locked, isPending, isPartOfValidWord, isDragOver, isBottomRow, isTopRow }) => {
  if (isPartOfValidWord) return '#2ecc71';
  if (isPending) return '#6310e0';
  if (locked) return '#541930';
  if (isDragOver) return '#dcd0f5';
  if (isBottomRow) return '#fbe6ee';
  if (isTopRow) return '#e6f8ee';
  return '#eee';
};

const Cell = styled.div`
  width: ${CELL_SIZE}px;
  height: ${CELL_SIZE}px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: bold;
  color: ${props => (props.locked || props.isPending ? 'white' : '#999')};
  background-color: ${cellBackground};
  border: 1px solid ${props => (props.isBottomRow ? '#f0b6cd' : props.isTopRow ? '#a9e6c2' : '#ddd')};
  cursor: ${props => (props.isPending ? 'pointer' : 'default')};
  transition: background-color 0.15s ease;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  min-height: 32px;
`;

const ActionButton = styled.button`
  padding: 6px 14px;
  font-size: 14px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WordPreview = styled.div`
  font-size: 15px;
  color: #333;
`;

const PathBox = styled(motion.div)`
  margin-top: 14px;
  width: 315px;
  max-width: 100%;
  text-align: left;
  border: 1px solid #ccc;
  padding: 12px;
  border-radius: 8px;
  color: #2c3e50;
  font-size: 15px;
  background-color: #f9f9f9;
`;

const DialogButton = styled.button`
  display: inline-block;
  padding: 12px 24px;
  background-color: #541930;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  margin-top: 0px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
  border: none;
  cursor: pointer;
  outline: none;
`;

// Builds the full contiguous run of occupied cells (locked tiles + pending tiles) along one
// line, so a new word can pick up letters from words already submitted (e.g. reusing the "E"
// in a locked "FADE" to spell "APE"). Returns null if there's a genuine empty gap.
function buildRun(board, pending, orientation, fixedCoord, minPos, maxPos) {
  const getIndex = (pos) => (
    orientation === 'horizontal' ? fixedCoord * GRID_SIZE + pos : pos * GRID_SIZE + fixedCoord
  );
  const getPending = (idx) => pending.find(p => p.index === idx);
  const isLocked = (idx) => board[idx] && board[idx].locked;

  for (let pos = minPos; pos <= maxPos; pos++) {
    const idx = getIndex(pos);
    if (!isLocked(idx) && !getPending(idx)) return null;
  }

  let start = minPos;
  while (start - 1 >= 0 && isLocked(getIndex(start - 1))) start -= 1;

  let end = maxPos;
  while (end + 1 < GRID_SIZE && isLocked(getIndex(end + 1))) end += 1;

  const indices = [];
  let word = '';
  for (let pos = start; pos <= end; pos++) {
    const idx = getIndex(pos);
    indices.push(idx);
    const pendingCell = getPending(idx);
    word += isLocked(idx) ? board[idx].letter : pendingCell.letter;
  }

  return { indices, word };
}

// For every newly-placed tile, checks the perpendicular direction for a "cross word" formed
// with existing locked tiles (e.g. placing tiles for "RAI" next to a locked "TOP" might also
// spell "IO" downward through the "O") — those cross words must be real words too.
function getCrossWords(board, pending, mainOrientation) {
  const crossOrientation = mainOrientation === 'horizontal' ? 'vertical' : 'horizontal';
  const crossWords = [];

  pending.forEach(p => {
    const row = Math.floor(p.index / GRID_SIZE);
    const col = p.index % GRID_SIZE;
    const fixedCoord = crossOrientation === 'horizontal' ? row : col;
    const pos = crossOrientation === 'horizontal' ? col : row;
    const run = buildRun(board, pending, crossOrientation, fixedCoord, pos, pos);
    if (run && run.indices.length >= 2) {
      crossWords.push(run);
    }
  });

  return crossWords;
}

function isAdjacentToLocked(board, index) {
  const r = Math.floor(index / GRID_SIZE);
  const c = index % GRID_SIZE;
  const neighbors = [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]];
  return neighbors.some(([nr, nc]) => {
    if (nr < 0 || nr >= GRID_SIZE || nc < 0 || nc >= GRID_SIZE) return false;
    const cell = board[nr * GRID_SIZE + nc];
    return cell && cell.locked;
  });
}

function getWordInfo(pending, board, hasPathStarted) {
  if (pending.length === 0) return null;

  let orientation;
  let fixedCoord;
  let minPos;
  let maxPos;

  if (pending.length >= 2) {
    const rows = pending.map(p => Math.floor(p.index / GRID_SIZE));
    const cols = pending.map(p => p.index % GRID_SIZE);
    const uniqueRows = new Set(rows);
    const uniqueCols = new Set(cols);

    if (uniqueRows.size === 1) {
      orientation = 'horizontal';
      fixedCoord = rows[0];
      minPos = Math.min(...cols);
      maxPos = Math.max(...cols);
    } else if (uniqueCols.size === 1) {
      orientation = 'vertical';
      fixedCoord = cols[0];
      minPos = Math.min(...rows);
      maxPos = Math.max(...rows);
    } else {
      return { valid: false, reason: 'not-in-line', indices: pending.map(p => p.index) };
    }
  } else {
    const row = Math.floor(pending[0].index / GRID_SIZE);
    const col = pending[0].index % GRID_SIZE;
    const hRun = buildRun(board, pending, 'horizontal', row, col, col);
    if (hRun && hRun.indices.length >= 2) {
      orientation = 'horizontal';
      fixedCoord = row;
      minPos = col;
      maxPos = col;
    } else {
      const vRun = buildRun(board, pending, 'vertical', col, row, row);
      if (vRun && vRun.indices.length >= 2) {
        orientation = 'vertical';
        fixedCoord = col;
        minPos = row;
        maxPos = row;
      } else {
        return null;
      }
    }
  }

  const run = buildRun(board, pending, orientation, fixedCoord, minPos, maxPos);
  if (!run) {
    return { valid: false, reason: 'gap', indices: pending.map(p => p.index) };
  }

  const { word, indices } = run;

  let placementOk;
  if (!hasPathStarted) {
    placementOk = pending.some(p => Math.floor(p.index / GRID_SIZE) === BOTTOM_ROW);
  } else {
    const reusesLockedTile = indices.some(i => board[i] && board[i].locked);
    const touchesLockedTile = pending.some(p => isAdjacentToLocked(board, p.index));
    placementOk = reusesLockedTile || touchesLockedTile;
  }

  const crossWords = getCrossWords(board, pending, orientation);

  return { word, indices, placementOk, crossWords, reason: placementOk ? null : 'not-connected' };
}

function Pebbles() {
  const [board, setBoard] = useState(() => Array(GRID_SIZE * GRID_SIZE).fill(null));
  const [rack, setRack] = useState(() => makeRack());
  const [pending, setPending] = useState([]);
  const [selectedRackIndex, setSelectedRackIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [moveCount, setMoveCount] = useState(0);
  const [pathWords, setPathWords] = useState([]);
  const [dictionary, setDictionary] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isCompletionOpen, setIsCompletionOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('./pebblesDictionary.json').then(mod => {
      if (!cancelled) setDictionary(new Set(mod.default));
    });
    return () => { cancelled = true; };
  }, []);

  const hasPathStarted = moveCount > 0;

  const wordInfo = useMemo(() => getWordInfo(pending, board, hasPathStarted), [pending, board, hasPathStarted]);
  const isDictionaryWord = wordInfo && wordInfo.word && dictionary && dictionary.has(wordInfo.word);
  const invalidCrossWords = useMemo(() => (
    wordInfo && wordInfo.crossWords && dictionary
      ? wordInfo.crossWords.filter(cw => !dictionary.has(cw.word))
      : []
  ), [wordInfo, dictionary]);
  const isSubmittable = Boolean(
    wordInfo && wordInfo.placementOk && isDictionaryWord && invalidCrossWords.length === 0
  );

  const occupiedIndex = useCallback((index) => {
    return Boolean(board[index]) || pending.some(p => p.index === index);
  }, [board, pending]);

  const placeLetter = useCallback((index, letter, rackIndex) => {
    setPending(prev => [...prev, { index, letter, rackIndex }]);
    setRack(prev => {
      const next = [...prev];
      next[rackIndex] = null;
      return next;
    });
  }, []);

  const handleRackTileClick = (index) => {
    if (rack[index] === null) return;
    setSelectedRackIndex(prev => (prev === index ? null : index));
  };

  const handleCellClick = (index) => {
    const pendingHere = pending.find(p => p.index === index);
    if (pendingHere) {
      setPending(prev => prev.filter(p => p.index !== index));
      setRack(prev => {
        const next = [...prev];
        next[pendingHere.rackIndex] = pendingHere.letter;
        return next;
      });
      return;
    }
    if (occupiedIndex(index)) return;
    if (selectedRackIndex === null || rack[selectedRackIndex] === null) return;

    placeLetter(index, rack[selectedRackIndex], selectedRackIndex);
    setSelectedRackIndex(null);
  };

  const handleDragStart = (e, rackIndex, letter) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ rackIndex, letter }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDragLeave = (index) => {
    setDragOverIndex(prev => (prev === index ? null : prev));
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (occupiedIndex(index)) return;

    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData('text/plain'));
    } catch {
      return;
    }
    if (!data || typeof data.rackIndex !== 'number') return;
    if (rack[data.rackIndex] === null) return;

    placeLetter(index, data.letter, data.rackIndex);
  };

  const clearPending = () => {
    setRack(prev => {
      const next = [...prev];
      pending.forEach(p => { next[p.rackIndex] = p.letter; });
      return next;
    });
    setPending([]);
  };

  const submitWord = () => {
    if (!isSubmittable) return;

    setBoard(prev => {
      const next = [...prev];
      pending.forEach(p => {
        next[p.index] = { letter: p.letter, locked: true };
      });
      return next;
    });
    setRack(prev => {
      const next = [...prev];
      pending.forEach(p => { next[p.rackIndex] = randomLetter(); });
      return next;
    });
    setPathWords(prev => [...prev, wordInfo.word]);
    setMoveCount(prev => prev + 1);

    const reachedTop = wordInfo.indices.some(i => Math.floor(i / GRID_SIZE) === TOP_ROW);
    setPending([]);

    if (reachedTop) {
      setIsCompletionOpen(true);

      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    }
  };

  const getCompletionMessage = (moves) => {
    if (moves <= 8) return "⚡ Lightning fast! ⚡";
    if (moves <= 14) return "🚀 Impressive route! 🚀";
    if (moves <= 20) return "🎯 Nice work! 🎯";
    return "🌟 You made it! 🌟";
  };

  const resetGame = () => {
    setBoard(Array(GRID_SIZE * GRID_SIZE).fill(null));
    setRack(makeRack());
    setPending([]);
    setSelectedRackIndex(null);
    setMoveCount(0);
    setPathWords([]);
    setIsCompletionOpen(false);
  };

  return (
    <WordGameContainer>
      <HeaderRow>
        <Title>Pebbles</Title>
        <AnimatedCoffeeButton
          onClick={() => window.open('https://buymeacoffee.com/coffeebreakgames', '_blank')}
          title="Buy me a coffee"
        >
          <FaCoffee />
        </AnimatedCoffeeButton>
      </HeaderRow>
      <Subtitle>Spell words to build a path from the bottom to the top</Subtitle>

      <StatusRow>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', color: '#2c3e50' }}>
          <FaRoute /> Moves: {moveCount}
        </div>
        <span onClick={() => setIsHelpOpen(true)} style={{ fontSize: '24px', color: '#2c3e50', cursor: 'pointer' }} title="How to play">
          <IoIosHelpCircle />
        </span>
      </StatusRow>

      <RackRow>
        {rack.map((letter, index) => (
          letter === null ? (
            <RackSlotEmpty key={index} />
          ) : (
            <RackTile
              key={index}
              draggable
              onDragStart={(e) => handleDragStart(e, index, letter)}
              onClick={() => handleRackTileClick(index)}
              isSelected={selectedRackIndex === index}
              whileTap={{ scale: 0.92 }}
            >
              {letter}
            </RackTile>
          )
        ))}
      </RackRow>

      <Board>
        {board.map((cell, index) => {
          const pendingHere = pending.find(p => p.index === index);
          const isPending = Boolean(pendingHere);
          const locked = Boolean(cell && cell.locked);
          const letter = locked ? cell.letter : (pendingHere ? pendingHere.letter : '');
          const row = Math.floor(index / GRID_SIZE);
          const isBottomRow = row === BOTTOM_ROW;
          const isTopRow = row === TOP_ROW;
          const isPartOfValidWord = isSubmittable && wordInfo.indices.includes(index);

          return (
            <Cell
              key={index}
              locked={locked}
              isPending={isPending}
              isPartOfValidWord={isPartOfValidWord}
              isDragOver={dragOverIndex === index && !occupiedIndex(index)}
              isBottomRow={isBottomRow && !locked && !isPending}
              isTopRow={isTopRow && !locked && !isPending}
              onClick={() => handleCellClick(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={() => handleDragLeave(index)}
              onDrop={(e) => handleDrop(e, index)}
            >
              {letter}
            </Cell>
          );
        })}
      </Board>

      <ActionRow>
        {pending.length > 0 && (
          <ActionButton onClick={clearPending}>Clear</ActionButton>
        )}
        <ActionButton onClick={submitWord} disabled={!isSubmittable}>Submit Word</ActionButton>
        <WordPreview>
          {wordInfo && wordInfo.word ? <strong>{wordInfo.word}</strong> : null}
        </WordPreview>
      </ActionRow>

      {!dictionary && (
        <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>Loading dictionary…</div>
      )}

      <PathBox
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <strong>Path so far:</strong>{' '}
        {pathWords.length > 0 ? pathWords.join(' → ') : 'None yet'}
      </PathBox>

      <Dialog.Root open={isCompletionOpen} onOpenChange={setIsCompletionOpen}>
        <Dialog.Portal>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
            <Dialog.Overlay style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(6px)',
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <motion.div
                initial={{ opacity: 0, rotateY: 10 }}
                animate={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.2, ease: 'easeIn' }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '32px 24px',
                  maxWidth: '320px',
                  width: '90%',
                  textAlign: 'center',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                }}
              >
                <h2 style={{ fontSize: '20px', marginBottom: '0px', marginTop: 0 }}>Reached the top in:</h2>
                <div style={{ fontSize: '50px', fontWeight: 'bold', color: '#541930', marginBottom: '12px' }}>
                  {moveCount} {moveCount === 1 ? 'move' : 'moves'}
                </div>
                <p style={{ fontSize: '16px', color: '#000', marginBottom: '24px', marginTop: 0 }}>
                  {getCompletionMessage(moveCount)}
                </p>
                <DialogButton onClick={resetGame}>Play again</DialogButton>
                <a
                  href="https://buymeacoffee.com/coffeebreakgames"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    marginTop: '12px',
                    color: 'black',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Support this game
                </a>
              </motion.div>
            </Dialog.Overlay>
          </motion.div>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <Dialog.Portal>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
            <Dialog.Overlay style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(6px)',
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <motion.div
                initial={{ opacity: 0, rotateY: 10 }}
                animate={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.2, ease: 'easeIn' }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '420px',
                  width: '90%',
                  textAlign: 'left',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                }}
              >
                <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>How to Play</h2>
                <div style={{ color: '#555', lineHeight: 1.6 }}>
                  <ul style={{ paddingLeft: '18px', marginTop: 0 }}>
                    <li>Click or drag a letter tile from the rack onto the empty board — its rack slot stays empty until you submit.</li>
                    <li>Place tiles in a single straight line (row or column) to spell a word — the tiles turn <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>green</span> once it's valid.</li>
                    <li>You can reuse letters already on your path — start a new word right next to (or through) one of your locked tiles.</li>
                    <li>If your new tiles accidentally spell another word crossing an existing one, that cross word has to be real too.</li>
                    <li>Your first word must touch the bottom row.</li>
                    <li>Every word after that must connect to your existing path.</li>
                    <li>Hit "Submit Word" to lock it in and refill your used tiles — that counts as one move.</li>
                    <li>Click a placed tile (or hit "Clear") to return it to your rack before submitting.</li>
                    <li>Reach the top row in as few moves as possible!</li>
                  </ul>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setIsHelpOpen(false)} style={{ marginTop: '12px', background: '#eee', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer' }}>Got it, let's play! 😎</button>
                </div>
              </motion.div>
            </Dialog.Overlay>
          </motion.div>
        </Dialog.Portal>
      </Dialog.Root>
    </WordGameContainer>
  );
}

export default Pebbles;
